/**
 * One brain for the computer opponent and the coach. Every number the coach
 * quotes comes from here, so the advice is exactly what the computer would do.
 *
 * Discard: exact expected hand value over every possible starter, plus (dealer)
 * or minus (pone) the thrown pair's expected crib value from the derived table,
 * tilted by board position (Colvert): ahead of par → play off, behind → play on.
 *
 * Pegging: immediate points, minus the expected points the opponent scores in
 * reply (one ply, uniform over unseen cards), plus small stated heuristics.
 */

import { type Card, combinations, fullDeck, rankOf, suitOf, valueOf } from './cards.js';
import { cribValue } from './cribTable.js';
import { type ScoreItem, scoreHand, scorePeg } from './scoring.js';

export interface Position {
  myScore: number;
  oppScore: number;
  /** Whether I deal this hand. */
  isDealer: boolean;
  /** 1-based hand number of the game. */
  handNo: number;
}

// ── board position (Colvert) ────────────────────────────────────────────────

/** Average points per deal: dealer ≈ 16 (hand ~8, crib ~4.7, pegging ~3.5), pone ≈ 10. */
export const DEALER_AVG = 16;
export const PONE_AVG = 10;

/**
 * Par: where an average player would stand at the START of this hand, given
 * who dealt each previous hand. First dealer's par before hand n alternates
 * 0, 16, 26, 42, 52, 68, 78, 94, 104...; first pone's 0, 10, 26, 36, 52, ...
 */
export function par(handNo: number, isDealerNow: boolean): number {
  let p = 0;
  for (let h = 1; h < handNo; h++) {
    // Dealer alternates; I deal now, so on hand h I dealt iff (handNo - h) is even.
    const dealtThen = (handNo - h) % 2 === 0 ? isDealerNow : !isDealerNow;
    p += dealtThen ? DEALER_AVG : PONE_AVG;
  }
  return p;
}

/**
 * Relative position: (me − my par) − (opp − their par). Positive = ahead of
 * par (play off / defensive), negative = behind (play on / offensive).
 */
export function parDelta(pos: Position): number {
  const mine = pos.myScore - par(pos.handNo, pos.isDealer);
  const theirs = pos.oppScore - par(pos.handNo, !pos.isDealer);
  return mine - theirs;
}

export type Mode = 'off' | 'on' | 'neutral';

/** Δ ≥ +5 → off (defensive); Δ ≤ −5 → on (offensive); else neutral. */
export function mode(pos: Position): Mode {
  const toMe = 121 - pos.myScore;
  const toOpp = 121 - pos.oppScore;
  if (toMe <= 16 || toOpp <= 16) {
    if (toMe < toOpp) return 'off';
    if (toOpp < toMe) return 'on';
    return 'neutral';
  }
  const d = parDelta(pos);
  return d >= 5 ? 'off' : d <= -5 ? 'on' : 'neutral';
}

/** A weight in [-1, 1]: +1 fully defensive, -1 fully offensive. */
export function stance(pos: Position): number {
  const m = mode(pos);
  return m === 'off' ? 1 : m === 'on' ? -1 : 0;
}

// ── discard ─────────────────────────────────────────────────────────────────

export interface DiscardOption {
  keep: Card[];
  throw: Card[];
  /** Exact expectation of the four kept cards over all possible starters. */
  handEV: number;
  /** Expected value of the two thrown cards in the crib (positive number). */
  cribEV: number;
  /** handEV ± cribEV, with the stance tilt applied. What the chooser ranks by. */
  total: number;
  /** Untilted: handEV + cribEV (dealer) or handEV − cribEV (pone). */
  net: number;
  minHand: number;
  maxHand: number;
  /** Points the four cards hold with no starter at all ("guaranteed"). */
  baseHand: number;
}

export function handExpectation(keep: readonly Card[], excluded: readonly Card[]): { ev: number; min: number; max: number } {
  const ex = new Set(excluded);
  let total = 0;
  let n = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const st of fullDeck()) {
    if (ex.has(st)) continue;
    const v = scoreHand(keep, st, false).total;
    total += v;
    n++;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { ev: total / n, min, max };
}

/** Score the four cards alone (as if the starter contributed nothing). */
export function baseValue(keep: readonly Card[]): number {
  // Use an impossible starter: a card not in the deck? scoreHand needs one.
  // Score the subsets directly instead.
  let pts = 0;
  for (let k = 2; k <= 4; k++)
    for (const combo of combinations(keep, k)) if (combo.reduce((s, c) => s + valueOf(c), 0) === 15) pts += 2;
  for (const [a, b] of combinations(keep, 2) as [Card, Card][]) if (rankOf(a) === rankOf(b)) pts += 2;
  if (new Set(keep.map(suitOf)).size === 1) pts += 4;
  const ranks = [...new Set(keep.map(rankOf))].sort((a, b) => a - b);
  // runs within 4 cards (with multiplicity)
  const byRank = new Map<number, number>();
  for (const c of keep) byRank.set(rankOf(c), (byRank.get(rankOf(c)) ?? 0) + 1);
  let i = 0;
  while (i < ranks.length) {
    let j = i;
    while (j + 1 < ranks.length && ranks[j + 1] === ranks[j]! + 1) j++;
    const len = j - i + 1;
    if (len >= 3) {
      let mult = 1;
      for (let r = i; r <= j; r++) mult *= byRank.get(ranks[r]!)!;
      pts += len * mult;
    }
    i = j + 1;
  }
  return pts;
}

export function evaluateDiscards(hand: readonly Card[], pos: Position, opts: { approx?: boolean } = {}): DiscardOption[] {
  if (hand.length !== 6) throw new Error('evaluateDiscards needs 6 cards');
  const w = stance(pos);
  const out: DiscardOption[] = [];
  for (const thrown of combinations(hand, 2)) {
    const keep = hand.filter((c) => !thrown.includes(c));
    // `approx` skips the 46-starter loop (used by the crib-table derivation,
    // where a million opponents must each pick a throw).
    const { ev, min, max } = opts.approx
      ? (() => {
          const b = baseValue(keep);
          return { ev: b + 2.2, min: b, max: b + 12 };
        })()
      : handExpectation(keep, hand);
    const crib = cribValue(thrown, pos.isDealer);
    const net = pos.isDealer ? ev + crib : ev - crib;
    // V(keep) = EH + s·λ·EC. Dealer: s=+1, λ=1 always. Pone: s=−1 and λ is
    // 1.25 when playing off (deny the crib), 0.8 when playing on, else 1.0.
    const lambda = pos.isDealer ? 1 : w > 0 ? 1.25 : w < 0 ? 0.8 : 1;
    const total = pos.isDealer ? ev + crib : ev - lambda * crib;
    out.push({ keep, throw: [...thrown], handEV: ev, cribEV: crib, total, net, minHand: min, maxHand: max, baseHand: baseValue(keep) });
  }
  out.sort((a, b) => b.total - a.total || b.net - a.net);
  return out;
}

export function chooseDiscard(
  hand: readonly Card[],
  isDealer: boolean,
  pos: { myScore: number; oppScore: number; handNo?: number },
  opts: { approx?: boolean } = {},
): DiscardOption {
  return evaluateDiscards(hand, { myScore: pos.myScore, oppScore: pos.oppScore, isDealer, handNo: pos.handNo ?? 1 }, opts)[0]!;
}

// ── pegging ─────────────────────────────────────────────────────────────────

export interface PegContext {
  /** My remaining cards. */
  hand: readonly Card[];
  /** The current sub-count stack, in order. */
  stack: readonly Card[];
  /** Every card I have seen: my original six, the starter, everything played. */
  seen: readonly Card[];
  /** Cards the opponent still holds. */
  oppCards: number;
  pos: Position;
  /** If known (I am pone and the starter is cut): my hand's show score. */
  myShowScore?: number;
}

export interface PegOption {
  card: Card;
  countAfter: number;
  /** Points I score now. */
  immediate: number;
  items: ScoreItem[];
  /** Expected points the opponent scores with their reply. */
  oppReply: number;
  /** Expected points my best follow-up scores after their reply (two-ply). */
  myReply: number;
  /** Endgame race term (0 unless either score ≥ 95). */
  race: number;
  /** Probability the opponent has no legal reply (I likely get a go point). */
  pGo: number;
  /** Named heuristics that applied, with their weights, for the coach to quote. */
  notes: { key: PegNoteKey; delta: number }[];
  total: number;
}

export type PegNoteKey =
  | 'lead-five'
  | 'lead-four'
  | 'lead-from-pair'
  | 'leaves-twenty-one'
  | 'leaves-five'
  | 'keeps-low-card'
  | 'magic-eleven'
  | 'safe-dump';

/** Unseen-card rank distribution given everything I have seen. */
export function unseenRanks(seen: readonly Card[]): number[] {
  const counts = Array(14).fill(0) as number[];
  for (let r = 1; r <= 13; r++) counts[r] = 4;
  for (const c of seen) counts[rankOf(c)]!--;
  return counts;
}

export function evaluatePegs(ctx: PegContext): PegOption[] {
  const { hand, stack, seen, pos } = ctx;
  const count = stack.reduce((s, c) => s + valueOf(c), 0);
  const legal = hand.filter((c) => count + valueOf(c) <= 31);
  const counts = unseenRanks(seen);
  const totalUnseen = counts.reduce((a, b) => a + b, 0);
  let w = stance(pos);
  const endgame = pos.myScore >= 95 || pos.oppScore >= 95;
  const needMe = 121 - pos.myScore;
  const needOpp = 121 - pos.oppScore;
  // "Who counts out at the show" switches (cribbage master, Gate A):
  // as pone, if my known hand already reaches 121 I will count first — only
  // pegging-out by the dealer can beat me, so defend fully. As dealer, if the
  // pone needs very little they will almost surely count out: defence is
  // worthless, press for every point.
  if (endgame && !pos.isDealer && ctx.myShowScore !== undefined && ctx.myShowScore >= needMe) w = 2;
  if (endgame && pos.isDealer && needOpp <= 4) w = -1;
  const out: PegOption[] = [];

  for (const card of legal) {
    const next = [...stack, card];
    const sc = scorePeg(next);
    const after = count + valueOf(card);
    const rest = hand.filter((c) => c !== card);

    // Two-ply: opponent's reply (uniform over unseen ranks) and my best
    // follow-up to each. Suit is irrelevant to pegging, so one representative
    // card per rank stands for all of them.
    let oppReply = 0;
    let myReply = 0;
    let pCanReply = 0;
    let pOppOut = 0;
    let pMeOutNext = 0;
    if (ctx.oppCards > 0 && totalUnseen > 0) {
      for (let r = 1; r <= 13; r++) {
        const n = counts[r]!;
        if (n <= 0) continue;
        const p = n / totalUnseen;
        const v = Math.min(r, 10);
        if (after + v > 31) continue;
        pCanReply += p;
        const rep = representative(r, seen, next);
        const afterOpp = [...next, rep];
        const oppPts = scorePeg(afterOpp).total;
        oppReply += p * oppPts;
        if (oppPts >= needOpp) pOppOut += p;
        let best = 0;
        const c2count = after + v;
        for (const c2 of rest) {
          if (c2count + valueOf(c2) > 31) continue;
          const pts = scorePeg([...afterOpp, c2]).total;
          if (pts > best) best = pts;
        }
        myReply += p * best;
        if (best >= needMe) pMeOutNext += p;
      }
    }
    // Chance NO card in their hand fits: (1 - pCanReply)^k for k cards.
    const pGo = ctx.oppCards > 0 ? Math.pow(1 - pCanReply, ctx.oppCards) : 1;
    // A go is worth 1 to me if I also cannot continue... approximate as 1 × pGo × 0.6.
    const goValue = after === 31 ? 0 : 0.6 * pGo;

    const notes: PegOption['notes'] = [];
    const push = (key: PegNoteKey, delta: number) => notes.push({ key, delta });

    if (count === 0) {
      if (rankOf(card) === 5) push('lead-five', -0.4); // already penalised by oppReply, but say it
      if (rankOf(card) === 4) push('lead-four', 0.15);
      if (rest.some((c) => rankOf(c) === rankOf(card)) && rankOf(card) !== 5) push('lead-from-pair', 0.35);
    }
    // Counts the opponent can hit with a ten-card. Only penalised when the card
    // itself scores nothing: three points now outweigh the expected give-back,
    // which oppReply already prices.
    if (after === 21 && sc.total === 0) push('leaves-twenty-one', -0.5);
    if (after === 5 && sc.total === 0) push('leaves-five', -0.4);
    // Keeping a low card for a late go / 31.
    if (rest.length > 0 && rest.some((c) => valueOf(c) <= 4) && valueOf(card) > 4) push('keeps-low-card', 0.15);
    // (The "magic eleven" is captured by the two-ply follow-up term; no
    // separate heuristic, so the coach never quotes it for a play it does not
    // explain.)
    // Dumping a high card when ahead (defensive) keeps the count high and short.
    if (w > 0.3 && valueOf(card) >= 10 && sc.total === 0 && oppReply < 0.3) push('safe-dump', 0.1);

    const heur = notes.reduce((s, n) => s + n.delta, 0);
    // Defensive stance weighs the opponent's reply more; offensive weighs own points more.
    const defW = 1 + 0.25 * Math.max(0, w);
    const offW = 1 + 0.3 * Math.max(0, -w);
    // Endgame race: reaching 121 now is everything; letting them reach it is fatal.
    let race = 0;
    if (endgame) {
      if (sc.total >= needMe) race += 100;
      race -= 100 * pOppOut * (w <= -1 && pos.isDealer && needOpp <= 4 ? 0 : 1);
      race += 50 * pMeOutNext;
    }
    const total = sc.total * offW - oppReply * defW + myReply * 0.8 + goValue + heur + race;
    out.push({ card, countAfter: after, immediate: sc.total, items: sc.items, oppReply, myReply, race, pGo, notes, total });
  }
  out.sort((a, b) => b.total - a.total);
  return out;
}

function representative(rank: number, seen: readonly Card[], stack: readonly Card[]): Card {
  const avoid = new Set([...seen, ...stack]);
  for (let s = 0; s < 4; s++) {
    const c = s * 13 + (rank - 1);
    if (!avoid.has(c)) return c;
  }
  return rank - 1;
}

export function choosePeg(ctx: PegContext): PegOption | null {
  const opts = evaluatePegs(ctx);
  return opts[0] ?? null;
}
